import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import KidService from "../../services/kids";
import { useNavigate } from "react-router-dom";
import decodeToken from "../../utils/decodeToken";
import onRazorpay from "./RazorPay";
import SubscriptionCheckout from "./SubscriptionCheckout";
import NoImg from "../../assets/no-image.png";
import moment from "moment";
import "./Checkout.css";
import { toast } from "react-toastify";

// Helper function to extract year from any date format
function getYearOfBirth(dateString) {
  if (!dateString) return null;

  // If it's already a 4-digit year
  if (/^\d{4}$/.test(dateString)) {
    return parseInt(dateString);
  }

  // Try parsing as date object
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return date.getFullYear();
  }

  // Try regex for common formats like DD-MM-YYYY or DD/MM/YYYY
  let matches;

  // Check for format DD-MM-YYYY or YYYY-MM-DD
  matches =
    dateString.match(/(\d{4})-\d{1,2}-\d{1,2}/) ||
    dateString.match(/\d{1,2}-\d{1,2}-(\d{4})/);
  if (matches && matches[1]) {
    return parseInt(matches[1]);
  }

  // Check for format DD/MM/YYYY or YYYY/MM/DD
  matches =
    dateString.match(/(\d{4})\/\d{1,2}\/\d{1,2}/) ||
    dateString.match(/\d{1,2}\/\d{1,2}\/(\d{4})/);
  if (matches && matches[1]) {
    return parseInt(matches[1]);
  }

  // Check for any 4-digit number that could be a year
  matches = dateString.match(/\b(19|20)\d{2}\b/);
  if (matches) {
    return parseInt(matches[0]);
  }

  return null;
}

const Toggle = ({ initialState = false, onChange }) => {
  const [isActive, setIsActive] = useState(initialState);

  const handleToggle = () => {
    const newState = !isActive;
    setIsActive(newState);
    if (onChange) {
      onChange(newState);
    }
  };

  const toggleStyle = {
    position: "relative",
    display: "inline-block",
    width: "50px",
    height: "26px",
    backgroundColor: isActive ? "#FDC513" : "#FFFFFF",
    borderRadius: "26px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    border: "1px solid #E0E0E0",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  };

  const circleStyle = {
    position: "absolute",
    top: "2px",
    left: isActive ? "26px" : "2px",
    width: "20px",
    height: "20px",
    backgroundColor: isActive ? "#FFFFFF" : "#FDC513",
    borderRadius: "50%",
    transition: "left 0.3s ease, background-color 0.3s ease",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
  };

  return (
    <div
      style={toggleStyle}
      onClick={handleToggle}
      role="switch"
      aria-checked={isActive}
    >
      <div style={circleStyle}></div>
    </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const [kid, setKid] = useState({});
  const [months, setMonths] = useState(0);
  const { user_id, full_name, email, mobile_no } = decodeToken();
  const [becomeMentor, setBecomeMentor] = useState(false);
  const [isSubscription, setIsSubscription] = useState(true); // Default to Auto-Pay (recommended)
  let { kid_id } = useParams();

  useEffect(() => {
    getKid();
  }, []);

  // Auto-disable subscription for 1 month (makes no sense for single payment)
  useEffect(() => {
    if (months === 1) {
      setIsSubscription(false);
    } else if (months > 1) {
      // Default to subscription for multi-month
      setIsSubscription(true);
    }
  }, [months]);

  let getKid = async () => {
    try {
      const { data } = await KidService.fetchKidById(kid_id);
      setKid(data.data[0]);
    } catch (err) {}
  };

  const handleMentorToggle = (state) => {
    setBecomeMentor(state);
  };

  const handleSubscriptionToggle = (state) => {
    setIsSubscription(state);
  };

  let totalAmt = months * kid.total_expenses;

  // Mentor data for subscription
  const mentorData = {
    user_id: user_id ? parseInt(user_id) : null,
    name: full_name,
    full_name: full_name,
    email: email,
    mobile_no: mobile_no,
  };

  let sponsorData = {
    kid_id: kid_id,
    kid_date_of_birth: getYearOfBirth(kid.date_of_birth)?.toString() || "",
    kid_name: kid.name,
    kid_bio: kid.bio,
    kid_gender: kid.gender,
    amount: totalAmt,
    mentor_id: parseInt(user_id),
    for_months: months,
    is_active: true,
    category_name: kid.category_name,
    state: kid.state,
    category_id: kid.category_id,
    mentor_name: full_name,
    email: email,
    is_anonymous: user_id ? false : true,
  };

  const handlePayment = async () => {
    if (!user_id) {
      navigate({
        pathname: `/anonymous/${sponsorData.kid_id}`,
        search: `?for_months=${months}`,
      });
    } else {
      onRazorpay(sponsorData);
    }
  };

  function AgeCalculator({ birthyear }) {
    const currentDate = new Date().getFullYear();
    const yearOfBirth = getYearOfBirth(kid.date_of_birth);

    if (!yearOfBirth) return 0;

    const age = currentDate - yearOfBirth;
    return age;
  }
  function ClassCalculator({ birthyear }) {
    const currentDate = new Date().getFullYear();
    const yearOfBirth = getYearOfBirth(kid.date_of_birth);
    if (!yearOfBirth) return 0;

    const age = currentDate - yearOfBirth;
    if (age <= 5) {
      return 0;
    }

    const studyingClass = age - 5;
    const cappedStudyingClass = Math.min(12, studyingClass);

    const studyCompletion = Math.round((cappedStudyingClass / 12) * 100);

    return studyCompletion;
  }

  const boxShadowStyle = {
    boxShadow: `
      0px 0px 8px 0px #50505080,
      0px 4px 6px 0px #75757580,
      0px 8px 12px 0px #E8E8E880,
      0px 0px 2px 0px #FDC51380,
      inset 0px 2px 4px 0px #00000065,
      inset 0px 0px 2px 0px #00000080,
      0px 1px 0px 0px #E1E1E180,
      0px -1px 0px 0px #FFFFFF80
    `,
  };

  return (
    <div className="sponsorModalContainer">
      <div className="headerBackground">
        <img src="/modalimages/sponsorModalBg.png" alt="profile-bg" />
      </div>
      <div className="sponsorModalSubContainer">
        <h3 className="sponsorHeading">Sponsor {kid.name}</h3>
        <div className="sponsorModalContent">
          <div className="sponsorModalContentLeft" style={boxShadowStyle}>
            <div className="leftContentHeader">
              <img src={kid.avatar_url || NoImg} className="kidImage" />
              <div className="kidInfo">
                <p className="kidNameLeft">{kid.name}</p>
                <p className="kidInfoText">
                  <AgeCalculator birthyear={kid.date_of_birth} /> /{kid.gender}
                </p>
              </div>
            </div>
            <div className="leftContentBody">
              <p className="leftBioHeading">Bio</p>
              <p className="leftBioText">{kid.bio}</p>
            </div>
            <div className="leftContentEducation">
              <div className="educationHeader">
                <p className="leftEducationHeading">Education Completed</p>
                <p className="leftEducationText">
                  {" "}
                  <ClassCalculator birthyear={kid.date_of_birth} />%
                </p>
              </div>
              <div
                className="educationBar"
                style={{
                  height: "10px",
                  width: "100%",
                  backgroundColor: "#E0E0E0",
                  borderRadius: "10px",
                  position: "relative",
                  marginTop: "8px",
                }}
              >
                <div
                  className="educationBarFill"
                  style={{
                    height: "100%",
                    width: `${ClassCalculator({
                      birthyear: kid.date_of_birth,
                    })}%`,
                    backgroundColor: "#F8CB36",
                    borderRadius: "10px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "#F8CB36",
                      position: "absolute",
                      top: "-5px",
                      right: "-10px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="leftContentBottom">
              <p className="leftEndComp">Aaalim</p>
              <p className="leftEndComp">Aaalim</p>
              <p className="leftEndComp">Aaalim</p>
            </div>
          </div>
          <div className="sponsorModalContentRight">
            <p className="contentRightHeading">Time Period 11</p>
            <p className="contentRightText">
              Select the time period for which you want to sponsor
            </p>
            <div className="timePeriodContainer" style={{ width: "100%" }}>
              <select
                className="form-control mb-3"
                style={{ width: "80%", color: "#757575" }}
                value={months}
                onChange={(e) => setMonths(e.target.value)}
              >
                <option value={0}>Select Time Period</option>
                <option value={1}>1 Month</option>
                <option value={2}>2 Months</option>
                <option value={3}>3 Months</option>
                <option value={4}>4 Months</option>
                <option value={5}>5 Months</option>
                <option value={6}>6 Months</option>
                <option value={7}>7 Months</option>
                <option value={8}>8 Months</option>
                <option value={9}>9 Months</option>
                <option value={10}>10 Months</option>
                <option value={11}>11 Months</option>
                <option value={12}>12 Months</option>
              </select>
            </div>
            {/* Payment Method Selection - Smart UI based on duration */}
            {months > 0 && (
              <div style={{ marginBottom: '20px' }}>
                {months === 1 ? (
                  /* Single Month - Just show payment summary */
                  <div style={{
                    background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #FFD54F',
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontSize: '14px', color: '#5D4037' }}>🎁 One-Time Gift</span>
                      <span style={{ 
                        fontSize: '18px', 
                        fontWeight: '700', 
                        color: '#E65100' 
                      }}>
                        ₹{kid.total_expenses?.toLocaleString()}
                      </span>
                    </div>
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#795548', 
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      💡 Want to make a lasting impact? Select <strong>2+ months</strong> to become a <strong>Monthly Guardian</strong> and ensure continuous support for {kid.name?.split(' ')[0] || 'this child'}'s education.
                    </p>
                  </div>
                ) : (
                  /* Multiple Months - Show payment options */
                  <div>
                    <p style={{ 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      color: '#555', 
                      marginBottom: '10px',
                    }}>
                      How would you like to support {kid.name?.split(' ')[0] || 'this child'}?
                    </p>
                    
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                      {/* Monthly Guardian - Recommended */}
                      <div 
                        onClick={() => setIsSubscription(true)}
                        style={{
                          border: isSubscription ? '2px solid #4CAF50' : '1.5px solid #e0e0e0',
                          borderRadius: '14px',
                          padding: '14px 16px',
                          cursor: 'pointer',
                          background: isSubscription 
                            ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)' 
                            : '#fff',
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          transform: isSubscription ? 'scale(1.01)' : 'scale(1)',
                          boxShadow: isSubscription 
                            ? '0 4px 20px rgba(76, 175, 80, 0.25)' 
                            : '0 2px 8px rgba(0,0,0,0.04)',
                        }}
                      >
                        {/* Most Impact Badge */}
                        <div style={{
                          position: 'absolute',
                          top: '-8px',
                          left: '16px',
                          background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                          color: '#fff',
                          padding: '3px 10px',
                          borderRadius: '10px',
                          fontSize: '9px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 6px rgba(76, 175, 80, 0.3)',
                        }}>
                          💚 Most Impact
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            border: isSubscription ? 'none' : '2px solid #bdbdbd',
                            background: isSubscription ? '#4CAF50' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {isSubscription && (
                              <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                            )}
                          </div>
                          
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              marginBottom: '2px'
                            }}>
                              <span style={{ 
                                fontSize: '15px', 
                                fontWeight: '600', 
                                color: isSubscription ? '#1B5E20' : '#333',
                              }}>
                                🤝 Become a Monthly Guardian
                              </span>
                              <span style={{ 
                                fontSize: '16px', 
                                fontWeight: '700', 
                                color: isSubscription ? '#2E7D32' : '#333',
                              }}>
                                ₹{kid.total_expenses?.toLocaleString()}<span style={{ fontSize: '11px', fontWeight: '400' }}>/mo</span>
                              </span>
                            </div>
                            <div style={{ 
                              fontSize: '11px', 
                              color: isSubscription ? '#388E3C' : '#888',
                              marginTop: '4px',
                              lineHeight: '1.4'
                            }}>
                              Ensure uninterrupted education every month
                            </div>
                            <div style={{ 
                              fontSize: '10px', 
                              color: isSubscription ? '#2E7D32' : '#999',
                              display: 'flex',
                              gap: '8px',
                              flexWrap: 'wrap',
                              marginTop: '6px'
                            }}>
                              <span style={{ 
                                background: isSubscription ? 'rgba(255,255,255,0.7)' : '#f5f5f5',
                                padding: '2px 6px',
                                borderRadius: '4px'
                              }}>📅 Auto-renews monthly</span>
                              <span style={{ 
                                background: isSubscription ? 'rgba(255,255,255,0.7)' : '#f5f5f5',
                                padding: '2px 6px',
                                borderRadius: '4px'
                              }}>🔓 Cancel anytime</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* One-Time Gift Card */}
                      <div 
                        onClick={() => setIsSubscription(false)}
                        style={{
                          border: !isSubscription ? '2px solid #FDC513' : '1.5px solid #e0e0e0',
                          borderRadius: '14px',
                          padding: '14px 16px',
                          cursor: 'pointer',
                          background: !isSubscription ? '#FFFDE7' : '#fafafa',
                          transition: 'all 0.2s ease',
                          boxShadow: !isSubscription 
                            ? '0 4px 15px rgba(253, 197, 19, 0.2)' 
                            : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            border: !isSubscription ? 'none' : '2px solid #bdbdbd',
                            background: !isSubscription ? '#FDC513' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {!isSubscription && (
                              <span style={{ color: '#333', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                            )}
                          </div>
                          
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                            }}>
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: !isSubscription ? '#5D4037' : '#666',
                              }}>
                                🎁 One-Time Gift
                              </span>
                              <span style={{ 
                                fontSize: '16px', 
                                fontWeight: '700', 
                                color: !isSubscription ? '#E65100' : '#666',
                              }}>
                                ₹{(months * (kid.total_expenses || 0)).toLocaleString()}
                              </span>
                            </div>
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#888',
                              marginTop: '2px'
                            }}>
                              Support {months} month{months > 1 ? 's' : ''} of education in one gift
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="becomeMentor">
              <div className="toggleButton">
                <Toggle
                  initialState={becomeMentor}
                  onChange={handleMentorToggle}
                />
              </div>
              <p className="mentorText">Become a Mentor</p>
            </div>
            {becomeMentor && (
              <div className="mentoringDesc">
                <p className="mentoringDescTitle">You are sponsoring:</p>
                <p className="mentoringDescInfo">
                  {kid.name} for {months} months
                </p>
              </div>
            )}

            {/* Show Subscription Checkout or One-Time Payment */}
            {isSubscription && months > 0 ? (
              <SubscriptionCheckout
                kidData={kid}
                monthsSelected={months}
                mentorData={mentorData}
                categoryId={kid.category_id}
                onSuccess={() => toast.success('Subscription created!')}
                onError={() => toast.error('Subscription failed')}
              />
            ) : (
              <div className="contentRightBottom">
                <button
                  className="NextButton"
                  onClick={() => handlePayment()}
                  disabled={months === 0}
                >
                  {isSubscription ? 'Select Duration First' : 'Next'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modalBottom">
        <div className="stepItem">
          <div className="stepNumber">1</div>
          <p className="stepLabel">Select Duration</p>
        </div>

        <div className="horizontalLine"></div>

        <div className="stepItem">
          <div className="stepNumber">2</div>
          <p className="stepLabel">Payment</p>
        </div>

        <div className="horizontalLine"></div>

        <div className="stepItem">
          <div className="stepNumber">3</div>
          <p className="stepLabel">Confirmation</p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
